/**
 * Wires the status-page incident feed into the shell:
 *   1. subscribes to the SignalR `portalincidentstatusmethod` hub messages
 *      (live updates) and pushes them into the incident store, and
 *   2. fetches the initial incident list once on start.
 *
 * Mirrors the legacy `AppContainerComponent` SignalR dispatch +
 * `IncidentStatusService.fetchInitialIncidents`. Skipped entirely for external
 * users — exactly like legacy (`portalincidentstatusmethod` is internal-only).
 *
 * Gated by `VITE_ENABLE_INCIDENT_STATUS` — off in dev/qa (nothing meaningful to
 * show for lower environments on the prod status page) and on elsewhere.
 */

import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { HubMethod, onPortalMessage } from '@/legacy/signal-r';
import { getTracker } from '@/telemetry/tracker';
import { getResolvedUserType, subscribeUserType } from '@/auth/user-type';
import { isEnvFlagTrue } from '@/config/env-flags';
import { normalizeIncidents, useIncidentStore } from './incident-store';

let started = false;
let waitingForUserType = false;

export function startIncidentStatus(): void {
  if (started) return;
  if (!isEnvFlagTrue(import.meta.env.VITE_ENABLE_INCIDENT_STATUS)) return;

  const userType = getResolvedUserType();
  if (!userType) {
    if (waitingForUserType) return;
    waitingForUserType = true;
    const unsubscribe = subscribeUserType(() => {
      const resolved = getResolvedUserType();
      if (!resolved) return;
      waitingForUserType = false;
      unsubscribe();
      startIncidentStatus();
    });
    return;
  }

  if (userType === 'external') return;
  started = true;

  // Live updates over SignalR. The connection negotiates asynchronously, so we
  // subscribe up front; messages only flow once the hub is connected.
  onPortalMessage((msg) => {
    if (msg.methodName !== HubMethod.PortalIncidentStatus) return;
    useIncidentStore.getState().setIncidents(normalizeIncidents(msg.result));
  });

  // One-shot initial load (legacy `fetchInitialIncidents`). Best-effort: a
  // failure just leaves the banner hidden until a SignalR push arrives.
  void http<unknown>(Endpoints.incidentStatus())
    .then((payload) => {
      useIncidentStore.getState().setIncidents(normalizeIncidents(payload));
    })
    .catch(() => {
      getTracker().event('incident_status_initial_fetch_failed');
    });
}
